import React, { useEffect, useState } from 'react';
// Material-UI imports
import {
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Accordion,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getTimeZoneForRegion } from '../talkTreeShared/Tree/TimezoneUtility'; 
import { INPUT_TYPE_TO_SCHOOL_YEAR, TALK_TREES_REGIONS } from '../api.js';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const AccordionComponent = ({
  inputType,
  setInputType,
  transcriptSessionID,
  setTranscriptSessionID,
  handleSessionIdChange,
  handleDataRetrievalClick,
  selectedRegion,
  handleselectedRegionChange,
  regionList,
  tutorList,
  tutorListError,
  setTutorList,
  setSelectedTutorId,
  handleDateChange,
  handleTutorChange,
  selectedTutorId,
  selectedDate,
  setSelectedDate,
  availablePeriods,
  selectedPeriod,
  handlePeriodChange,
  fetchAvailablePeriods,
  isTutorFeedbackPage,
  isStudentEngagementPage,
  urlTutorId,
  tutorName,
  client,
  mostRecentSession,
  oldestSession,
}) => {
  // Add state for the timezone
  const [currentTimezone, setCurrentTimezone] = useState("America/Chicago"); // Default timezone
  // Add loading state for periods
  const [isLoadingPeriods, setIsLoadingPeriods] = useState(false);
  // State to track if the period dropdown is open
  const [, setIsPeriodDropdownOpen] = useState(false);
  // Data Viewer only: the date the period list was last retrieved for. The date
  // field reports every segment change, so retrieval waits for the Apply click.
  const [appliedDate, setAppliedDate] = useState('');

  const isDataViewer = !isTutorFeedbackPage && !isStudentEngagementPage;
  const dateNeedsApplying = isDataViewer && Boolean(selectedDate) && selectedDate !== appliedDate;
    const theme = createTheme({
      typography: {
        fontFamily: '"Mulish",sans-serif',
      },
    });

  // Update timezone when region changes
  useEffect(() => {
    if (selectedRegion) {
      const newTimezone = getTimeZoneForRegion(selectedRegion);
      setCurrentTimezone(newTimezone);
    }
  }, [selectedRegion]);

  // Fetch available periods once a date is settled on: the Apply click
  const periodFetchTrigger = isDataViewer ? appliedDate : selectedDate;

  useEffect(() => {
    if (!selectedDate) {
      setAppliedDate('');
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedTutorId && periodFetchTrigger) {
      setIsLoadingPeriods(true);
      fetchAvailablePeriods().finally(() => {
        setIsLoadingPeriods(false);
      });
    }
  }, [selectedTutorId, periodFetchTrigger]);

  useEffect(() => {
    // If we have a urlTutorId and mostRecentSession data, auto-select that session
    if ((isTutorFeedbackPage || isStudentEngagementPage) && urlTutorId && mostRecentSession && mostRecentSession.startTime) {
      // Format the date to YYYY-MM-DD for the date picker
      const sessionDate = new Date(mostRecentSession.startTime);
      //console.log("Most recent session date:", sessionDate);
      //const formattedDate = sessionDate.toISOString().split('T')[0];   // uses UTC timezone; may be a day ahead!
      const formattedDate = new Intl.DateTimeFormat('en-CA').format(sessionDate);  // en-CA indicates YYYY-MM-DD format

      // Auto-select the date
      setSelectedDate(formattedDate);

      // This will trigger fetchAvailablePeriods via the useEffect that watches selectedDate
    }
  }, [isTutorFeedbackPage, isStudentEngagementPage, urlTutorId, mostRecentSession]);

  useEffect(() => {
    // Only proceed if we're in tutor feedback page with a most recent session
    if ((isTutorFeedbackPage || isStudentEngagementPage) && mostRecentSession && availablePeriods.length > 0) {
      // Find the period that matches the most recent session
      const matchingPeriod = availablePeriods.find(
        period => period.sessionId === mostRecentSession.sessionId
      );

      if (matchingPeriod) {
        // Auto-select the period
        handlePeriodChange({ target: { value: matchingPeriod.id } });
      }
    }
  }, [isTutorFeedbackPage, isStudentEngagementPage, mostRecentSession, availablePeriods]);

  // Create a modified tutor change handler that resets date and period
  const handleTutorChangeWithReset = (e) => {
    handleTutorChange(e);
    const emptyEvent = { target: { value: "" } };
    handleDateChange(emptyEvent);
    handlePeriodChange(emptyEvent);
    setAppliedDate('');
  };

  // Create a modified region change handler that resets tutor, date and period
  const handleRegionChangeWithReset = (e) => {
    handleselectedRegionChange(e);

    // Clear tutor list and set loading state
    setTutorList([]);
    setSelectedTutorId('');

    // Reset tutor, date and period to null/empty
    const emptyEvent = { target: { value: "" } };
    handleTutorChange(emptyEvent);
    handleDateChange(emptyEvent);
    handlePeriodChange(emptyEvent);
    setAppliedDate('');
  };


  // Enhanced date change handler with timezone consideration
  const handleApplyDateClick = () => {
    setAppliedDate(selectedDate);
  };

  const handleDateChangeWithTimezone = (e) => {
    handleDateChange(e);

    // Drop any period chosen under the previous date so a stale session cannot
    // be retrieved while the new date is still unapplied.
    if (isDataViewer) {
      handlePeriodChange({ target: { value: "" } });
    }

    if (isTutorFeedbackPage || isStudentEngagementPage) {
      const formattedDate = `Date: ${e.target.value}`;
      mixpanel.track("dateChanged", {
        selectedDate: formattedDate,
      });
    }
  };

  // Format time display with timezone
  const formatTimeWithTimezone = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: currentTimezone
    });
  };

  const formatDateWithTimezone = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    return date.toLocaleDateString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: currentTimezone
    });
  };

  // Add this useEffect in your AccordionComponent
  useEffect(() => {
    // If transcriptSessionID is set from period selection, trigger data retrieval
    if (transcriptSessionID && transcriptSessionID.length > 0 && selectedPeriod) {
      handleDataRetrievalClick();
    }
  }, [transcriptSessionID, selectedPeriod]);

  // Then simplify your handlePeriodChangeWithSessionUpdate
  const handlePeriodChangeWithSessionUpdate = (e) => {
    const selectedValue = e.target.value;
    handlePeriodChange(e);

    if (selectedValue) {
      const selectedPeriodObj = availablePeriods.find(p => p.id === selectedValue);
      if (selectedPeriodObj && selectedPeriodObj.sessionId) {
        setTranscriptSessionID(selectedPeriodObj.sessionId);
      }

      if (isTutorFeedbackPage || isStudentEngagementPage) {
        mixpanel.track("periodChanged", {
          selectedPeriod: selectedValue,
          sessionId: selectedPeriodObj.sessionId,
          sessionTitle: selectedPeriodObj.title,
          startTime: selectedPeriodObj.startTime,
        });
      }
    }
  };

  // Handlers for period dropdown open/close
  const handlePeriodDropdownOpen = () => {
    setIsPeriodDropdownOpen(true);
    // If we have a tutor and date but no periods loaded yet, start loading
    if (selectedTutorId && selectedDate && availablePeriods.length === 0 && !isLoadingPeriods) {
      setIsLoadingPeriods(true);
      fetchAvailablePeriods().finally(() => {
        setIsLoadingPeriods(false);
      });
    }
  };

  const handlePeriodDropdownClose = () => {
    setIsPeriodDropdownOpen(false);
  };

  // Modify the renderValue for the period Select to handle different scenarios
  const renderPeriodValue = (selected) => {
    // If periods are loading
    if (isLoadingPeriods) {
      return 'Loading periods...';
    }

    // If no periods are available
    if (availablePeriods.length === 0) {
      return 'No periods available';
    }

    // If a period is selected
    const period = availablePeriods.find(p => p.id === selected);
    if (period) {
      const timeString = formatTimeWithTimezone(period.startTime);
      return `${timeString} - ${period.title}`;
    }

    // Default case: prompt to select a period
    return '-- Select Period --';
  };

  return (
    <ThemeProvider theme={theme}>

      {!isTutorFeedbackPage && !isStudentEngagementPage && <Accordion disableGutters style={{ height: "20%", backgroundColor: '#F9F7F6', border: '1px solid #E0DEDC', borderRadius: '4px', marginBottom: '10px' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          style={{ minHeight: '36px', padding: '0 16px' }}
        >
          <Typography>Select by Session Id: {transcriptSessionID}</Typography>
        </AccordionSummary>
        <AccordionDetails style={{ paddingTop: '8px', paddingBottom: '8px' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <FormControl size="small" style={{ width: '40ch' }}>
                <InputLabel id="region-label">Select Client and School Year</InputLabel>
                <Select
                  size="small"
                  labelId="input-label"
                  id="selectedinputtype"
                  value={inputType}
                  onChange={(e) => setInputType(e.target.value)}
                  label="Select Client and School Year"
                >
                  {Object.entries(INPUT_TYPE_TO_SCHOOL_YEAR).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <TextField size="small" style={{ width: "80ch" }}
                id="transcriptSessionID"
                label="Session Id"
                type="text"
                value={transcriptSessionID}
                onChange={handleSessionIdChange}
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleDataRetrievalClick}
                style={{
                  textTransform: "none",
                  borderRadius: "6px",
                  backgroundColor: "#1F72E7",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  padding: "8px 16px",
                }}
              >
                Retrieve Data
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>}


      <Accordion defaultExpanded disableGutters
        style={{ height: "20%", textAlign: (isTutorFeedbackPage || isStudentEngagementPage) ? "left" : "center", boxShadow: (isTutorFeedbackPage || isStudentEngagementPage) ? "none" : "0 2px 4px rgba(0,0,0,0.1)", backgroundColor: "#F9F7F6", border: (isTutorFeedbackPage || isStudentEngagementPage) ? undefined : '1px solid #E0DEDC', borderRadius: (isTutorFeedbackPage || isStudentEngagementPage) ? undefined : '4px' }}
        {...((isTutorFeedbackPage || isStudentEngagementPage) && {
          expanded: true,
          onChange: () => { } // Prevent collapse
        })}>
        <AccordionSummary
          expandIcon={!(isTutorFeedbackPage || isStudentEngagementPage) ? <ExpandMoreIcon /> : null}
          aria-controls="panel2a-content"
          id="panel2a-header"
          style={{
            cursor: (isTutorFeedbackPage || isStudentEngagementPage) ? 'default' : 'pointer',
            minHeight: (isTutorFeedbackPage || isStudentEngagementPage) ? '10px !important' : '36px',
            padding: (isTutorFeedbackPage || isStudentEngagementPage) ? undefined : '0 16px',
          }}
          sx={(isTutorFeedbackPage || isStudentEngagementPage) ? {
            minHeight: '10px !important',
            height: '10px !important',
            padding: '0 !important',
            '& .MuiAccordionSummary-content': {
              minHeight: '10px !important',
              height: '10px !important',
              margin: '0 !important'
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              display: 'none !important'
            }
          } : undefined}
        >
          {!((isTutorFeedbackPage || isStudentEngagementPage) && urlTutorId) && (
            <Typography>
              {`Select by Tutor or Teacher ${selectedRegion ? `(${selectedRegion} - ${currentTimezone})` : ''}`}
            </Typography>
          )}
        </AccordionSummary>
        <AccordionDetails style={{ marginLeft: '10px', paddingTop: '8px', paddingBottom: '8px' }}>
          <Grid container spacing={2} alignItems="center">
            {(isTutorFeedbackPage || isStudentEngagementPage) && urlTutorId ? (
              // Display tutor info when in TutorFeedback mode with urlTutorId
              // Display a Welcome <tutor name> message unless the client is "Demo"
              <Grid item xs={12}>

                  {client !== "Demo" && client !== "TeachFX" && (
                  <Typography variant="h7" style={{  fontSize: '14px', fontFamily: '"Mulish",sans-serif'}}>
                    <strong>Welcome {tutorName || selectedTutorId}</strong>!
                  </Typography>
                  )}
                  {(client === "Demo" || client === "TeachFX") && (
                    <Typography variant="body1" style={{ lineHeight: 1.75, fontSize: '14px', fontFamily: '"Mulish",sans-serif' }}>
                      <strong>Welcome!</strong>
                    </Typography>
                  )}
                  {/* {isStudentEngagementPage && (
                    <Typography variant="body1" style={{ lineHeight: 1.75, fontSize: '14px' }}>
                        Have a look at <a href='/TalkTreeResources' target='_blank' rel='noopener noreferrer'>these resources</a> to get started.
                    </Typography>
                  )} */}


                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginTop: '23px' }}>
                  {selectedTutorId && isTutorFeedbackPage && (
                    <div style={{
                      width: '38ch',
                      padding: '6px',
                      backgroundColor: '#f6f6f6',
                      borderRadius: '3px'
                    }}>
                      <Typography variant="body2" color="textSecondary">
                        <b>Most recent</b>: {formatDateWithTimezone(mostRecentSession.startTime)}<br />
                        <b>Title</b>: {mostRecentSession.sessionTitle || 'N/A'}
                      </Typography>
                    </div>
                  )}
                  <TextField
                    style={{ width: "220px" }}
                    id="start-date"
                    label={`Select Date (${selectedRegion ? selectedRegion : 'Local'} Time)`}
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChangeWithTimezone}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    // Add the max attribute when on tutor feedback page
                    inputProps={{
                      max: (isTutorFeedbackPage || isStudentEngagementPage) && mostRecentSession?.startTime
                        ? new Date(mostRecentSession.startTime).toISOString().split('T')[0]
                        : undefined,
                      min: (isTutorFeedbackPage || isStudentEngagementPage) && oldestSession?.startTime
                        ? new Date(oldestSession.startTime).toISOString().split('T')[0]
                        : undefined
                    }}
                  />
                  <FormControl style={{ width: '400px' }}>
                    <InputLabel id="period-label" >
                      {isLoadingPeriods
                        ? 'Loading Periods..'
                        : availablePeriods.length === 0
                          ? 'No Periods Available'
                          : 'Select Period'}
                    </InputLabel>
                    <Select
                      labelId="period-label"
                      id="selectedPeriod"
                      value={selectedPeriod}
                      onChange={handlePeriodChangeWithSessionUpdate}
                      onOpen={handlePeriodDropdownOpen}
                      onClose={handlePeriodDropdownClose}
                      label="Select Period"
                      renderValue={renderPeriodValue}
                    >
                      {isLoadingPeriods ? (
                        <MenuItem disabled value="">
                          <div style={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
                            <CircularProgress size={20} style={{ marginRight: '10px' }} />
                            <span>Loading periods...</span>
                          </div>
                        </MenuItem>
                      ) : availablePeriods.length > 0 ? (
                        availablePeriods.map((period) => {
                          // Format the time based on the selected region's timezone
                          const timeString = formatTimeWithTimezone(period.startTime);
                          let studentDisplay = '';
                          if ((isTutorFeedbackPage || isStudentEngagementPage) && period.students && period.students.length > 0) {
                            // For tutor feedback page, we want to show student names instead of IDs
                            // We need to map the student IDs to names using the participants data
                            const studentNames = period.students.map(studentId => {
                              // Find the participant that matches this student ID
                              const participant = period.sessionData?.sessionMetadata?.participants?.find(
                                p => p.id === studentId && p.type === 'student'
                              );
                              return participant ? participant.name : studentId; // Fallback to ID if name not found
                            });
                            studentDisplay = studentNames.join(', ');
                          } else {
                            // For other pages, just show the raw student IDs if available
                            studentDisplay = period.students ? period.students.join(', ') : '';
                          }
                          return (
                            <MenuItem key={period.id} value={period.id} style={{ padding: '8px 16px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ fontWeight: 'bold' }}>{timeString}</span>
                                  {selectedPeriod === period.id && (
                                    <span style={{ color: '#1976d2', fontSize: '0.9em' }}>(Selected)</span>
                                  )}
                                </div>
                                <span style={{ color: '#444', marginTop: '4px' }}>{period.title}</span>
                                {period.students && period.students.length > 0 && (
                                  <span style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                    Students: {studentDisplay}
                                  </span>
                                )}
                              </div>
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem disabled value="">
                          No periods available
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </div>
              </Grid>
            ) : (
              // Regular region and tutor selection fields
              <>
                <Grid item>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '15px' }}>
                    <FormControl size="small" style={{ width: '250px' }}>
                      <InputLabel id="talktrees-pipeline-label">Select Talk Trees pipeline</InputLabel>
                      <Select
                        labelId="talktrees-pipeline-label"
                        id="selectedTalkTreesPipeline"
                        value={TALK_TREES_REGIONS[selectedRegion] ? selectedRegion : ''}
                        onChange={handleRegionChangeWithReset}
                        label="Select Talk Trees pipeline"
                      >
                        {regionList.filter((option) => TALK_TREES_REGIONS[option]).map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Typography variant="caption" style={{ textAlign: 'center', color: '#666', lineHeight: 1 }}>or</Typography>

                    <FormControl size="small" style={{ width: '250px' }}>
                      <InputLabel id="saga-region-label">Select Saga region</InputLabel>
                      <Select
                        labelId="saga-region-label"
                        id="selectedSagaRegion"
                        value={!TALK_TREES_REGIONS[selectedRegion] ? selectedRegion : ''}
                        onChange={handleRegionChangeWithReset}
                        label="Select Saga region"
                      >
                        {regionList.filter((option) => !TALK_TREES_REGIONS[option]).map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </Grid>
                <Grid item>
                  <FormControl size="small" style={{ width: '300px', marginBottom: '15px' }}>
                    <InputLabel id="tutor-select-label">
                      {tutorListError || (TALK_TREES_REGIONS[selectedRegion] ? 'Select Teacher' : 'Select Tutor')}
                    </InputLabel>
                    <Select
                      size="small"
                      labelId="tutor-select-label"
                      value={selectedTutorId}
                      onChange={handleTutorChangeWithReset}
                      label={tutorListError || (TALK_TREES_REGIONS[selectedRegion] ? 'Select Teacher' : 'Select Tutor')}
                      renderValue={(selected) => {
                        const tutor = tutorList.find(t => t.userId === selected);
                        if (!tutor) return '';
                        return `${tutor.name} - ${tutor.readableDate}`;
                      }}
                    >
                      {tutorList.map((tutor) => (
                        <MenuItem key={tutor.userId} value={tutor.userId} style={{ padding: '8px 16px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 'bold' }}>{tutor.name}</span>
                              {selectedTutorId === tutor.userId && (
                                <span style={{ color: '#1976d2', fontSize: '0.9em' }}>(Selected)</span>
                              )}
                            </div>
                            <span style={{ fontSize: '0.85em', color: '#666', marginTop: '0px' }}>
                              Most recent: {tutor.readableDate}
                            </span>
                            <span style={{ fontSize: '0.85em', color: '#666', marginTop: '0px' }}>
                              Count: {tutor.sessionCount}
                            </span>
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {!(isTutorFeedbackPage || isStudentEngagementPage) && (
              <>
                <Grid item>
                  <TextField
                    size="small"
                    style={{ width: "300px", marginBottom: '15px' }}
                    id="start-date"
                    label={`Select Date (${selectedRegion ? selectedRegion : 'Local'} Time)`}
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChangeWithTimezone}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            size="small"
                            onClick={handleApplyDateClick}
                            disabled={!dateNeedsApplying}
                            style={{ textTransform: "none", minWidth: "auto" }}
                          >
                            Apply
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item>
                  <FormControl size="small" style={{ width: '400px', marginBottom: '15px' }}>
                    <InputLabel id="period-label" >
                      {dateNeedsApplying
                        ? 'Apply date to load periods'
                        : isLoadingPeriods
                          ? 'Loading Periods..'
                          : availablePeriods.length === 0
                            ? 'No Periods Available'
                            : 'Select Period'}
                    </InputLabel>
                    <Select
                      size="small"
                      labelId="period-label"
                      id="selectedPeriod"
                      value={selectedPeriod}
                      onChange={handlePeriodChangeWithSessionUpdate}
                      onOpen={handlePeriodDropdownOpen}
                      onClose={handlePeriodDropdownClose}
                      label="Select Period"
                      renderValue={renderPeriodValue}
                      disabled={dateNeedsApplying}
                    >
                      {isLoadingPeriods ? (
                        <MenuItem disabled value="">
                          <div style={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
                            <CircularProgress size={20} style={{ marginRight: '10px' }} />
                            <span>Loading periods...</span>
                          </div>
                        </MenuItem>
                      ) : availablePeriods.length > 0 ? (
                        availablePeriods.map((period) => {
                          // Format the time based on the selected region's timezone
                          const timeString = formatTimeWithTimezone(period.startTime);
                          let studentDisplay = '';
                          if ((isTutorFeedbackPage || isStudentEngagementPage) && period.students && period.students.length > 0) {
                            // For tutor feedback page, we want to show student names instead of IDs
                            // We need to map the student IDs to names using the participants data
                            const studentNames = period.students.map(studentId => {
                              // Find the participant that matches this student ID
                              const participant = period.sessionData?.sessionMetadata?.participants?.find(
                                p => p.id === studentId && p.type === 'student'
                              );
                              return participant ? participant.name : studentId; // Fallback to ID if name not found
                            });
                            studentDisplay = studentNames.join(', ');
                          } else {
                            // For other pages, just show the raw student IDs if available
                            studentDisplay = period.students ? period.students.join(', ') : '';
                          }
                          return (
                            <MenuItem key={period.id} value={period.id} style={{ padding: '8px 16px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ fontWeight: 'bold' }}>{timeString}</span>
                                  {selectedPeriod === period.id && (
                                    <span style={{ color: '#1976d2', fontSize: '0.9em' }}>(Selected)</span>
                                  )}
                                </div>
                                <span style={{ color: '#444', marginTop: '4px' }}>{period.title}</span>
                                {period.students && period.students.length > 0 && (
                                  <span style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                    Students: {studentDisplay}
                                  </span>
                                )}
                              </div>
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem disabled value="">
                          No periods available
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

    </ThemeProvider>
  );
};

export default AccordionComponent;
